import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';

export default function CouponManagement() {
  const [activeTab, setActiveTab] = useState('MANAGEMENT'); // 'MANAGEMENT' | 'ANALYTICS'

  // Management State
  const [coupons, setCoupons] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters & Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [facilityFilter, setFacilityFilter] = useState('ALL');

  // Analytics State
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [facilityAnalytics, setFacilityAnalytics] = useState([]);
  const [couponAnalytics, setCouponAnalytics] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewCoupon, setPreviewCoupon] = useState(null);
  const [editingCoupon, setEditingCoupon] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    code: '',
    description: '',
    discount_type: 'FLAT',
    discount_value: '',
    min_order_value: '0',
    max_discount_amount: '',
    max_redemptions: '1000',
    per_user_limit: '1',
    valid_from: '',
    valid_until: '',
    account_id: '00000000-0000-0000-0000-000000000001',
    facility_id: '',
  });

  const getAuthHeaders = useCallback(() => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('admin_token') ||
      localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }, []);

  // Fetch Facilities List
  const fetchFacilities = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/facilities/`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setFacilities(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to fetch facilities list', err);
    }
  }, [getAuthHeaders]);

  // Fetch Commercial Coupons List
  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/coupons/`;
      const queryParams = [];
      if (statusFilter !== 'ALL') {
        queryParams.push(`status=${statusFilter}`);
      }
      if (facilityFilter !== 'ALL') {
        queryParams.push(`facility_id=${facilityFilter}`);
      }
      if (queryParams.length > 0) {
        url += `?${queryParams.join('&')}`;
      }

      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to fetch commercial coupons');
      }
      const data = await res.json();
      setCoupons(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, facilityFilter, getAuthHeaders]);

  // Fetch Analytics Overview & Breakdown
  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      let overviewUrl = `${API_BASE_URL}/coupons/analytics/overview`;
      let facUrl = `${API_BASE_URL}/coupons/analytics/facilities`;
      let cpnUrl = `${API_BASE_URL}/coupons/analytics/coupons`;

      if (facilityFilter !== 'ALL') {
        overviewUrl += `?facility_id=${facilityFilter}`;
        facUrl += `?facility_id=${facilityFilter}`;
        cpnUrl += `?facility_id=${facilityFilter}`;
      }

      const [resO, resF, resC] = await Promise.all([
        fetch(overviewUrl, { headers: getAuthHeaders() }),
        fetch(facUrl, { headers: getAuthHeaders() }),
        fetch(cpnUrl, { headers: getAuthHeaders() }),
      ]);

      if (resO.ok) {
        const dataO = await resO.json();
        setAnalyticsOverview(dataO);
      }
      if (resF.ok) {
        const dataF = await resF.json();
        setFacilityAnalytics(dataF);
      }
      if (resC.ok) {
        const dataC = await resC.json();
        setCouponAnalytics(dataC);
      }
    } catch (err) {
      setAnalyticsError(err.message);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [facilityFilter, getAuthHeaders]);

  useEffect(() => {
    fetchFacilities();
  }, [fetchFacilities]);

  useEffect(() => {
    if (activeTab === 'MANAGEMENT') {
      fetchCoupons();
    } else if (activeTab === 'ANALYTICS') {
      fetchAnalytics();
    }
  }, [activeTab, fetchCoupons, fetchAnalytics]);

  // Facilities Lookup Map
  const facilityMap = React.useMemo(() => {
    const map = {};
    facilities.forEach((f) => {
      map[f.id] = f.name;
    });
    return map;
  }, [facilities]);

  // Client-Side Search Filtering
  const filteredCoupons = React.useMemo(() => {
    if (!searchTerm.trim()) return coupons;
    const term = searchTerm.toLowerCase().trim();
    return coupons.filter(
      (c) =>
        (c.code && c.code.toLowerCase().includes(term)) ||
        (c.title && c.title.toLowerCase().includes(term)) ||
        (facilityMap[c.facility_id] && facilityMap[c.facility_id].toLowerCase().includes(term))
    );
  }, [coupons, searchTerm, facilityMap]);

  const handleOpenCreateModal = () => {
    setEditingCoupon(null);
    setFormData({
      title: '',
      code: '',
      description: '',
      discount_type: 'FLAT',
      discount_value: '',
      min_order_value: '0',
      max_discount_amount: '',
      max_redemptions: '1000',
      per_user_limit: '1',
      valid_from: new Date().toISOString().slice(0, 16),
      valid_until: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
      account_id: '',
      facility_id: facilities.length > 0 ? facilities[0].id : '',
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      title: coupon.title || '',
      code: coupon.code || '',
      description: coupon.description || '',
      discount_type: coupon.discount_type || 'FLAT',
      discount_value: coupon.discount_value || '',
      min_order_value: coupon.min_order_value || '0',
      max_discount_amount: coupon.max_discount_amount || '',
      max_redemptions: coupon.max_redemptions || '1000',
      per_user_limit: coupon.per_user_limit || '1',
      valid_from: coupon.valid_from ? new Date(coupon.valid_from).toISOString().slice(0, 16) : '',
      valid_until: coupon.valid_until ? new Date(coupon.valid_until).toISOString().slice(0, 16) : '',
      account_id: coupon.account_id || '',
      facility_id: coupon.facility_id || '',
    });
    setShowModal(true);
  };

  const handleOpenPreviewModal = (coupon) => {
    setPreviewCoupon(coupon);
    setShowPreviewModal(true);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    setFacilityFilter('ALL');
  };

  const parseResponseSafely = async (res, defaultErrorMsg) => {
    if (!res.ok) {
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        try {
          const errData = await res.json();
          const detailMsg = typeof errData.detail === 'string'
            ? errData.detail
            : (Array.isArray(errData.detail) ? errData.detail.map(d => d.msg).join(', ') : null);
          throw new Error(detailMsg || defaultErrorMsg);
        } catch (e) {
          if (e.message && e.message !== defaultErrorMsg && !e.message.includes('JSON')) {
            throw e;
          }
        }
      }
      const textBody = await res.text().catch(() => '');
      const statusInfo = `${res.status} ${res.statusText}`.trim();
      const bodySnippet = textBody ? textBody.trim().slice(0, 100) : '';
      const errorDetail = bodySnippet ? `${statusInfo}: ${bodySnippet}` : statusInfo;
      throw new Error(`${defaultErrorMsg}: ${errorDetail}`);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCoupon) {
        const payload = {
          title: formData.title,
          description: formData.description || null,
          discount_type: formData.discount_type,
          discount_value: parseFloat(formData.discount_value),
          min_order_value: parseFloat(formData.min_order_value || '0'),
          max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : null,
          max_redemptions: parseInt(formData.max_redemptions, 10),
          per_user_limit: parseInt(formData.per_user_limit, 10),
          valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
          valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null,
        };

        const res = await fetch(`${API_BASE_URL}/coupons/${editingCoupon.id}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });

        await parseResponseSafely(res, 'Failed to update offer');
      } else {
        if (!formData.facility_id) {
          throw new Error('Please select a facility for this commercial offer');
        }

        const payload = {
          title: formData.title,
          code: formData.code,
          description: formData.description || null,
          discount_type: formData.discount_type,
          discount_value: parseFloat(formData.discount_value),
          min_order_value: parseFloat(formData.min_order_value || '0'),
          max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : null,
          max_redemptions: parseInt(formData.max_redemptions, 10),
          per_user_limit: parseInt(formData.per_user_limit, 10),
          valid_from: formData.valid_from ? new Date(formData.valid_from).toISOString() : null,
          valid_until: new Date(formData.valid_until).toISOString(),
          facility_id: formData.facility_id,
        };

        if (formData.account_id && formData.account_id !== '00000000-0000-0000-0000-000000000001') {
          payload.account_id = formData.account_id;
        }

        const res = await fetch(`${API_BASE_URL}/coupons/`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        });

        await parseResponseSafely(res, 'Failed to create offer');
      }

      setShowModal(false);
      fetchCoupons();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleStatusTransition = async (coupon, action) => {
    let confirmMsg = `Are you sure you want to ${action} commercial offer '${coupon.code}'?`;
    if (action === 'retire') {
      confirmMsg = `⚠️ DESTRUCTIVE ACTION: Are you sure you want to RETIRE offer '${coupon.code}'? This offer will be permanently archived and cannot be reactivated again!`;
    }

    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await fetch(`${API_BASE_URL}/coupons/${coupon.id}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      await parseResponseSafely(res, `Failed to ${action} offer`);

      fetchCoupons();
    } catch (err) {
      alert(`Status Transition Error: ${err.message}`);
    }
  };

  // QR Redemption State
  const [verifyPayload, setVerifyPayload] = useState('');
  const [verifyFacilityId, setVerifyFacilityId] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyError, setVerifyError] = useState(null);

  const handleVerifyRedeem = async (e) => {
    e.preventDefault();
    if (!verifyPayload.trim()) {
      alert('Please enter or scan a QR Payload or Redemption Code');
      return;
    }

    setVerifyLoading(true);
    setVerifyResult(null);
    setVerifyError(null);

    try {
      const payload = {
        qr_payload: verifyPayload.trim(),
        facility_id: verifyFacilityId || null,
      };

      const res = await fetch(`${API_BASE_URL}/coupons/verify`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await parseResponseSafely(res, 'Verification failed');
      setVerifyResult(data);
    } catch (err) {
      setVerifyError(err.message);
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <div className="commercial-module" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2>🎟️ Commercial Offers & Performance Console</h2>
          <p style={{ color: '#666', marginTop: '4px' }}>
            Manage commercial promotion campaigns, track real-time redemption metrics, and evaluate facility conversion rates.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={activeTab === 'MANAGEMENT' ? fetchCoupons : fetchAnalytics}
            style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: '10px 14px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            🔄 Refresh
          </button>
          {activeTab === 'MANAGEMENT' && (
            <button
              className="btn-primary"
              onClick={handleOpenCreateModal}
              style={{ backgroundColor: '#FF5722', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
            >
              + Create Commercial Offer
            </button>
          )}
        </div>
      </div>

      {/* Tabs Header */}
      <div style={{ display: 'flex', borderBottom: '2px solid #eee', marginBottom: '20px' }}>
        <button
          onClick={() => setActiveTab('MANAGEMENT')}
      style={{
        padding: '10px 20px',
        border: 'none',
        borderBottom: activeTab === 'MANAGEMENT' ? '3px solid #FF5722' : 'none',
        backgroundColor: 'transparent',
        color: activeTab === 'MANAGEMENT' ? '#FF5722' : '#666',
        fontWeight: 'bold',
        fontSize: '15px',
        cursor: 'pointer',
      }}
    >
      🎟️ Offers Management
    </button>
    <button
      onClick={() => setActiveTab('ANALYTICS')}
      style={{
        padding: '10px 20px',
        border: 'none',
        borderBottom: activeTab === 'ANALYTICS' ? '3px solid #FF5722' : 'none',
        backgroundColor: 'transparent',
        color: activeTab === 'ANALYTICS' ? '#FF5722' : '#666',
        fontWeight: 'bold',
        fontSize: '15px',
        cursor: 'pointer',
      }}
    >
      📊 Commercial Analytics & KPIs
    </button>
    <button
      onClick={() => setActiveTab('REDEMPTION')}
      style={{
        padding: '10px 20px',
        border: 'none',
        borderBottom: activeTab === 'REDEMPTION' ? '3px solid #FF5722' : 'none',
        backgroundColor: 'transparent',
        color: activeTab === 'REDEMPTION' ? '#FF5722' : '#666',
        fontWeight: 'bold',
        fontSize: '15px',
        cursor: 'pointer',
      }}
    >
      📱 Verify & Redeem QR Code
    </button>
  </div>

      {/* OFFERS MANAGEMENT TAB */}
      {activeTab === 'MANAGEMENT' && (
        <>
          {/* Filter Bar & Search */}
          <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ flex: '1 1 300px' }}>
                <input
                  type="text"
                  placeholder="🔍 Search by coupon code or title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                {['ALL', 'ACTIVE', 'INACTIVE', 'RETIRED'].map((st) => (
                  <button
                    key={st}
                    onClick={() => setStatusFilter(st)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      border: '1px solid #ccc',
                      backgroundColor: statusFilter === st ? '#FF5722' : '#f5f5f5',
                      color: statusFilter === st ? '#fff' : '#333',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                    }}
                  >
                    {st}
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontWeight: 'bold', fontSize: '13px', color: '#555' }}>📍 Facility:</label>
                <select
                  value={facilityFilter}
                  onChange={(e) => setFacilityFilter(e.target.value)}
                  style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff' }}
                >
                  <option value="ALL">All Facilities</option>
                  {facilities.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} ({f.facility_type})
                    </option>
                  ))}
                </select>
              </div>

              {(searchTerm || statusFilter !== 'ALL' || facilityFilter !== 'ALL') && (
                <button
                  onClick={handleClearFilters}
                  style={{ padding: '6px 12px', borderRadius: '4px', border: 'none', backgroundColor: '#eeeeee', cursor: 'pointer', color: '#555', fontSize: '12px', fontWeight: 'bold' }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {error && (
            <div style={{ color: '#d32f2f', backgroundColor: '#ffebee', padding: '12px', borderRadius: '4px', marginBottom: '20px' }}>
              {error}
            </div>
          )}

          {loading ? (
            <p>Loading commercial offers...</p>
          ) : (
            <div className="card" style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left', padding: '8px' }}>
                    <th style={{ padding: '10px' }}>Code</th>
                    <th style={{ padding: '10px' }}>Offer Title</th>
                    <th style={{ padding: '10px' }}>Facility</th>
                    <th style={{ padding: '10px' }}>Discount</th>
                    <th style={{ padding: '10px' }}>Status</th>
                    <th style={{ padding: '10px' }}>Validity Window</th>
                    <th style={{ padding: '10px' }}>Redemptions (Usage)</th>
                    <th style={{ padding: '10px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCoupons.map((c) => {
                    const remaining = Math.max(0, c.max_redemptions - c.current_redemptions);
                    return (
                      <tr key={c.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px', fontFamily: 'monospace', fontWeight: 'bold', fontSize: '14px' }}>{c.code}</td>
                        <td style={{ padding: '10px' }}>{c.title}</td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ backgroundColor: '#e3f2fd', color: '#1565c0', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                            📍 {facilityMap[c.facility_id] || (c.facility_id ? c.facility_id.slice(0, 8) : 'All Facilities')}
                          </span>
                        </td>
                        <td style={{ padding: '10px', fontWeight: 'bold' }}>
                          {c.discount_type === 'PERCENTAGE' ? `${c.discount_value}% OFF` : `₹${c.discount_value} OFF`}
                        </td>
                        <td style={{ padding: '10px' }}>
                          <span
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              backgroundColor:
                                c.status === 'ACTIVE' ? '#e8f5e9' : c.status === 'INACTIVE' ? '#fff3e0' : '#ffebee',
                              color:
                                c.status === 'ACTIVE' ? '#2e7d32' : c.status === 'INACTIVE' ? '#e65100' : '#c62828',
                            }}
                          >
                            {c.status}
                          </span>
                        </td>
                        <td style={{ padding: '10px', fontSize: '13px' }}>
                          {c.valid_from ? new Date(c.valid_from).toLocaleDateString() : 'Immediate'} →{' '}
                          {new Date(c.valid_until).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '10px', fontSize: '13px' }} title={`${remaining} redemptions remaining`}>
                          <strong>{c.current_redemptions} / {c.max_redemptions}</strong> used
                          <br />
                          <span style={{ fontSize: '11px', color: '#666' }}>({remaining} remaining)</span>
                        </td>
                        <td style={{ padding: '10px' }}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              onClick={() => handleOpenPreviewModal(c)}
                              style={{ padding: '4px 8px', backgroundColor: '#e0f7fa', color: '#006064', border: '1px solid #b2ebf2', borderRadius: '3px', cursor: 'pointer', fontSize: '12px' }}
                              title="Pilgrim Offer Preview"
                            >
                              👁️ Preview
                            </button>
                            {c.status === 'INACTIVE' && (
                              <>
                                <button onClick={() => handleOpenEditModal(c)} style={{ padding: '4px 8px', cursor: 'pointer' }}>Edit</button>
                                <button onClick={() => handleStatusTransition(c, 'activate')} style={{ padding: '4px 8px', backgroundColor: '#4caf50', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Publish</button>
                                <button onClick={() => handleStatusTransition(c, 'retire')} style={{ padding: '4px 8px', backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Retire</button>
                              </>
                            )}
                            {c.status === 'ACTIVE' && (
                              <>
                                <button onClick={() => handleOpenEditModal(c)} style={{ padding: '4px 8px', cursor: 'pointer' }}>Edit</button>
                                <button onClick={() => handleStatusTransition(c, 'deactivate')} style={{ padding: '4px 8px', backgroundColor: '#ff9800', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Pause</button>
                                <button onClick={() => handleStatusTransition(c, 'retire')} style={{ padding: '4px 8px', backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Retire</button>
                              </>
                            )}
                            {c.status === 'RETIRED' && <span style={{ color: '#999', fontSize: '12px', padding: '4px' }}>Archived</span>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredCoupons.length === 0 && (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '24px', color: '#888' }}>
                        No commercial offers found matching current search or filters. Click "+ Create Commercial Offer" to register a new promotion.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* COMMERCIAL ANALYTICS TAB */}
      {activeTab === 'ANALYTICS' && (
        <>
          {analyticsError && (
            <div style={{ color: '#d32f2f', backgroundColor: '#ffebee', padding: '12px', borderRadius: '4px', marginBottom: '20px' }}>
              Error loading analytics: {analyticsError}
              <button onClick={fetchAnalytics} style={{ marginLeft: '12px', padding: '4px 8px', cursor: 'pointer' }}>Retry</button>
            </div>
          )}

          {analyticsLoading ? (
            <p>Loading commercial analytics and metrics...</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* KPI Summary Cards */}
              {analyticsOverview && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                  <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', borderLeft: '4px solid #2196f3', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <span style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>TOTAL OFFERS</span>
                    <h3 style={{ margin: '8px 0 0 0', fontSize: '24px', color: '#222' }}>{analyticsOverview.total_coupons}</h3>
                    <span style={{ fontSize: '11px', color: '#4caf50' }}>{analyticsOverview.active_coupons} Active</span>
                  </div>

                  <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', borderLeft: '4px solid #ff9800', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <span style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>TOTAL CLAIMS</span>
                    <h3 style={{ margin: '8px 0 0 0', fontSize: '24px', color: '#222' }}>{analyticsOverview.total_claims}</h3>
                    <span style={{ fontSize: '11px', color: '#888' }}>{analyticsOverview.unique_pilgrims} Unique Pilgrims</span>
                  </div>

                  <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', borderLeft: '4px solid #4caf50', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <span style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>REDEMPTIONS</span>
                    <h3 style={{ margin: '8px 0 0 0', fontSize: '24px', color: '#2e7d32' }}>{analyticsOverview.successful_redemptions}</h3>
                    <span style={{ fontSize: '11px', color: '#2e7d32', fontWeight: 'bold' }}>{analyticsOverview.conversion_rate}% Conversion</span>
                  </div>

                  <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', borderLeft: '4px solid #9c27b0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <span style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>REMAINING CAPACITY</span>
                    <h3 style={{ margin: '8px 0 0 0', fontSize: '24px', color: '#222' }}>{analyticsOverview.remaining_redemption_capacity}</h3>
                    <span style={{ fontSize: '11px', color: '#666' }}>of {analyticsOverview.total_max_redemptions} max</span>
                  </div>

                  <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', borderLeft: '4px solid #00bcd4', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <span style={{ fontSize: '12px', color: '#666', fontWeight: 'bold' }}>COUPON UTILIZATION</span>
                    <h3 style={{ margin: '8px 0 0 0', fontSize: '24px', color: '#00838f' }}>{analyticsOverview.coupon_utilization_pct}%</h3>
                    <span style={{ fontSize: '11px', color: '#666' }}>Average Capacity Used</span>
                  </div>
                </div>
              )}

              {/* Facility Performance Table */}
              <div className="card" style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#333' }}>📍 Facility Commercial Performance</h3>
                <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                      <th style={{ padding: '10px' }}>Facility Name</th>
                      <th style={{ padding: '10px' }}>Type</th>
                      <th style={{ padding: '10px' }}>Active Offers</th>
                      <th style={{ padding: '10px' }}>Claims</th>
                      <th style={{ padding: '10px' }}>Redemptions</th>
                      <th style={{ padding: '10px' }}>Conversion %</th>
                      <th style={{ padding: '10px' }}>Utilization %</th>
                      <th style={{ padding: '10px' }}>Remaining Capacity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facilityAnalytics.map((fa) => (
                      <tr key={fa.facility_id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px', fontWeight: 'bold' }}>{fa.facility_name}</td>
                        <td style={{ padding: '10px', textTransform: 'capitalize' }}>{fa.facility_type}</td>
                        <td style={{ padding: '10px' }}>{fa.active_offers_count}</td>
                        <td style={{ padding: '10px' }}>{fa.total_claims}</td>
                        <td style={{ padding: '10px', color: '#2e7d32', fontWeight: 'bold' }}>{fa.successful_redemptions}</td>
                        <td style={{ padding: '10px', fontWeight: 'bold' }}>{fa.conversion_rate}%</td>
                        <td style={{ padding: '10px' }}>{fa.utilization_percentage}%</td>
                        <td style={{ padding: '10px' }}>{fa.remaining_capacity}</td>
                      </tr>
                    ))}
                    {facilityAnalytics.length === 0 && (
                      <tr>
                        <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                          No facility performance records available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Top Coupon Performance Table */}
              <div className="card" style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#333' }}>🎟️ Top Offers & Coupons Analytics</h3>
                <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                      <th style={{ padding: '10px' }}>Code</th>
                      <th style={{ padding: '10px' }}>Title</th>
                      <th style={{ padding: '10px' }}>Facility</th>
                      <th style={{ padding: '10px' }}>Status</th>
                      <th style={{ padding: '10px' }}>Claims</th>
                      <th style={{ padding: '10px' }}>Redemptions</th>
                      <th style={{ padding: '10px' }}>Conversion %</th>
                      <th style={{ padding: '10px' }}>Utilization %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {couponAnalytics.map((ca) => (
                      <tr key={ca.coupon_id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px', fontFamily: 'monospace', fontWeight: 'bold' }}>{ca.code}</td>
                        <td style={{ padding: '10px' }}>{ca.title}</td>
                        <td style={{ padding: '10px' }}>📍 {ca.facility_name}</td>
                        <td style={{ padding: '10px' }}>
                          <span style={{ padding: '3px 6px', borderRadius: '3px', fontSize: '11px', fontWeight: 'bold', backgroundColor: ca.status === 'ACTIVE' ? '#e8f5e9' : '#fff3e0', color: ca.status === 'ACTIVE' ? '#2e7d32' : '#e65100' }}>
                            {ca.status}
                          </span>
                        </td>
                        <td style={{ padding: '10px' }}>{ca.total_claims}</td>
                        <td style={{ padding: '10px', color: '#2e7d32', fontWeight: 'bold' }}>{ca.successful_redemptions}</td>
                        <td style={{ padding: '10px', fontWeight: 'bold' }}>{ca.conversion_rate}%</td>
                        <td style={{ padding: '10px' }}>{ca.utilization_percentage}%</td>
                      </tr>
                    ))}
                    {couponAnalytics.length === 0 && (
                      <tr>
                        <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                          No coupon performance analytics available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* VERIFY & REDEEM QR TAB */}
      {activeTab === 'REDEMPTION' && (
        <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', maxWidth: '650px', margin: '0 auto' }}>
          <div style={{ borderBottom: '2px solid #FF5722', paddingBottom: '12px', marginBottom: '20px' }}>
            <span style={{ fontSize: '12px', textTransform: 'uppercase', color: '#FF5722', fontWeight: 'bold', letterSpacing: '1px' }}>📱 Facility QR Code Scanner & Manual Redemption</span>
            <h3 style={{ margin: '6px 0 0 0', color: '#222' }}>Verify Pilgrim Coupon Redemption</h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
              Scan or paste the pilgrim's QR Payload string or enter their unique Redemption Code (e.g. <code>RED-BOLBAM0001-XXXX</code>) to verify eligibility and mark as REDEEMED.
            </p>
          </div>

          <form onSubmit={handleVerifyRedeem} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '6px', color: '#333' }}>
                QR Payload or Redemption Code *
              </label>
              <textarea
                required
                rows={3}
                placeholder="Scan barcode / QR string (e.g. BOLBAM:COUPON:...) or enter Redemption Code (RED-XXXX...)"
                value={verifyPayload}
                onChange={(e) => setVerifyPayload(e.target.value)}
                style={{ width: '100%', padding: '10px', fontSize: '14px', fontFamily: 'monospace', borderRadius: '6px', border: '1px solid #ccc' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '6px', color: '#333' }}>
                Merchant Facility Context (Optional / Auto-scoped)
              </label>
              <select
                value={verifyFacilityId}
                onChange={(e) => setVerifyFacilityId(e.target.value)}
                style={{ width: '100%', padding: '10px', fontSize: '14px', borderRadius: '6px', border: '1px solid #ccc' }}
              >
                <option value="">Auto-Detect / Facility Authorized ▼</option>
                {facilities.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name} ({f.facility_type})
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={verifyLoading}
              style={{
                backgroundColor: '#FF5722',
                color: '#fff',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '6px',
                fontWeight: 'bold',
                fontSize: '15px',
                cursor: verifyLoading ? 'not-allowed' : 'pointer',
                marginTop: '8px',
              }}
            >
              {verifyLoading ? '⏳ Verifying & Redeeming...' : '⚡ Confirm & Redeem Coupon'}
            </button>
          </form>

          {verifyError && (
            <div style={{ marginTop: '20px', backgroundColor: '#ffebee', color: '#c62828', padding: '14px', borderRadius: '8px', borderLeft: '4px solid #c62828', fontWeight: 'bold' }}>
              ❌ Verification Failed: {verifyError}
            </div>
          )}

          {verifyResult && (
            <div style={{ marginTop: '20px', backgroundColor: '#e8f5e9', color: '#2e7d32', padding: '20px', borderRadius: '8px', borderLeft: '4px solid #2e7d32' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>✅ Coupon Verified & Redeemed!</span>
                <span style={{ backgroundColor: '#4caf50', color: '#fff', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold' }}>STATUS: REDEEMED</span>
              </div>

              <div style={{ fontSize: '14px', display: 'flex', flexDirection: 'column', gap: '6px', color: '#1b5e20' }}>
                <div><strong>Offer Code:</strong> <code>{verifyResult.coupon_code}</code></div>
                <div>
                  <strong>Discount Applied:</strong>{' '}
                  {verifyResult.discount_type === 'PERCENTAGE' ? `${verifyResult.discount_value}% OFF` : `₹${verifyResult.discount_value} OFF`}
                </div>
                <div><strong>Redeemed At:</strong> {new Date(verifyResult.redeemed_at).toLocaleString()}</div>
                <div><strong>Message:</strong> {verifyResult.message}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create / Edit Modal Form */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '8px', width: '550px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3>{editingCoupon ? 'Edit Commercial Offer' : 'Create New Commercial Offer'}</h3>
            <form onSubmit={handleFormSubmit} style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Offer Title *</label>
                <input type="text" required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} style={{ width: '100%', padding: '8px' }} />
              </div>

              {!editingCoupon && (
                <div>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Coupon Code * (Uppercase)</label>
                  <input type="text" required value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} style={{ width: '100%', padding: '8px' }} />
                </div>
              )}

              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Facility *</label>
                {editingCoupon ? (
                  <div>
                    <input
                      type="text"
                      disabled
                      value={facilityMap[editingCoupon.facility_id] || editingCoupon.facility_id || 'N/A'}
                      style={{ width: '100%', padding: '8px', backgroundColor: '#eee', cursor: 'not-allowed' }}
                    />
                    <span style={{ fontSize: '11px', color: '#888', marginTop: '2px', display: 'block' }}>
                      Facility cannot be changed after offer creation.
                    </span>
                  </div>
                ) : (
                  <select
                    required
                    value={formData.facility_id}
                    onChange={(e) => setFormData({ ...formData, facility_id: e.target.value })}
                    style={{ width: '100%', padding: '8px' }}
                  >
                    <option value="">Select Facility ▼</option>
                    {facilities.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name} ({f.facility_type})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Discount Type *</label>
                  <select value={formData.discount_type} onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })} style={{ width: '100%', padding: '8px' }}>
                    <option value="FLAT">FLAT (₹)</option>
                    <option value="PERCENTAGE">PERCENTAGE (%)</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Discount Value *</label>
                  <input type="number" step="any" required value={formData.discount_value} onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })} style={{ width: '100%', padding: '8px' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Min Order Value (₹)</label>
                  <input type="number" step="any" value={formData.min_order_value} onChange={(e) => setFormData({ ...formData, min_order_value: e.target.value })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Max Redemptions *</label>
                  <input type="number" required value={formData.max_redemptions} onChange={(e) => setFormData({ ...formData, max_redemptions: e.target.value })} style={{ width: '100%', padding: '8px' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Valid From</label>
                  <input type="datetime-local" value={formData.valid_from} onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })} style={{ width: '100%', padding: '8px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Valid Until *</label>
                  <input type="datetime-local" required value={formData.valid_until} onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })} style={{ width: '100%', padding: '8px' }} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '4px' }}>Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} style={{ width: '100%', padding: '8px', rows: 3 }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '8px 16px', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '8px 16px', backgroundColor: '#FF5722', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
                  {editingCoupon ? 'Save Changes' : 'Create Offer (Draft)'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pilgrim Offer Preview Modal */}
      {showPreviewModal && previewCoupon && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '12px', width: '420px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
            <div style={{ borderBottom: '2px solid #FF5722', paddingBottom: '12px', marginBottom: '16px' }}>
              <span style={{ fontSize: '11px', textTransform: 'uppercase', color: '#FF5722', fontWeight: 'bold', letterSpacing: '1px' }}>📱 Pilgrim Mobile Offer Preview</span>
              <h3 style={{ margin: '6px 0 0 0', color: '#222' }}>{previewCoupon.title}</h3>
            </div>

            <div style={{ backgroundColor: '#f9f9f9', padding: '16px', borderRadius: '8px', border: '1px dashed #FF5722', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#FF5722' }}>
                  {previewCoupon.discount_type === 'PERCENTAGE' ? `${previewCoupon.discount_value}% OFF` : `₹${previewCoupon.discount_value} OFF`}
                </span>
                <span style={{ fontFamily: 'monospace', fontWeight: 'bold', backgroundColor: '#fff', padding: '4px 8px', border: '1px solid #FF5722', borderRadius: '4px', color: '#333' }}>
                  {previewCoupon.code}
                </span>
              </div>
              <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#555' }}>
                📍 <strong>{facilityMap[previewCoupon.facility_id] || 'Target Facility'}</strong>
              </p>
              {previewCoupon.description && (
                <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666', italic: 'true' }}>
                  "{previewCoupon.description}"
                </p>
              )}
              <div style={{ fontSize: '11px', color: '#888', borderTop: '1px solid #eee', paddingTop: '8px', marginTop: '8px' }}>
                <div>• Minimum order: ₹{previewCoupon.min_order_value}</div>
                <div>• Valid: {new Date(previewCoupon.valid_until).toLocaleDateString()}</div>
                <div>• Limit: {previewCoupon.per_user_limit} redemption per pilgrim</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPreviewModal(false)}
                style={{ padding: '8px 16px', backgroundColor: '#333', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
